"use client";

import Link from "next/link";
import { useState } from "react";
import { Search, ShoppingCart } from "lucide-react";
import { SearchForm } from "@/components/search/SearchForm";
import { SearchModal } from "@/components/search/SearchModal";
import { AccountMenu } from "./AccountMenu";
import { ReportProblem } from "@/components/issues/ReportProblem";
import { langPath, type Lang } from "@/lib/i18n";
import { LangToggle } from "./LangToggle";
import { useDict } from "@/components/i18n/DictProvider";

export function AppTopBar({
  lang,
  userName,
  userEmail,
  isSubscription = false,
  cartBadge,
  languages,
  aiChatEnabled = false,
  issueReports = false,
  portalName,
}: {
  lang: Lang;
  userName: string;
  /** "Signed in as" line in the account menu. */
  userEmail?: string;
  /** Portal `paymentModel` — gates the subscriptions entry in the account menu. */
  isSubscription?: boolean;
  /** Server-streamed cart count badge (see CartBadge) — a slot, so the
   *  no-store cart read never blocks the shell's first paint. */
  cartBadge?: React.ReactNode;
  /** Enabled portal locales for the language switcher (O8). */
  languages: string[];
  /** Portal ai_chat toggle — shows the assistant-as-search entry. */
  aiChatEnabled?: boolean;
  /** Portal `issue_reports` — the "report a problem" icon. */
  issueReports?: boolean;
  /** The report panel says whose team the report goes to. */
  portalName: string;
}) {
  const t = useDict();
  const [searchOpen, setSearchOpen] = useState(false);
  return (
    <>
      {/* Catalog search (O19): Enter submits to /search, which offers the
          assistant as escalation where the portal has one. The placeholder
          says SEARCH on purpose — BrainAsk shares the dashboard screen and
          owns the "ask" promise. On every portal: /search works without the
          assistant, and gating it on ai_chat left members of such a portal
          with no search at all (2026-10-19 QA). */}
      <SearchForm
        lang={lang}
        placeholder={t.search.placeholder}
        className="mr-3 hidden max-w-sm flex-1 sm:block"
      />
      {/* Brand now lives at the top of the sidebar (AppShellClient); the topbar
          spans the content area only and carries the account/utility controls. */}
      <div className="ml-auto flex items-center gap-1 sm:gap-3">
        {aiChatEnabled ? (
          // Opens the same SearchModal palette as the marketing header (one
          // learned behavior across surfaces); href is the no-JS fallback.
          <Link
            href={langPath(lang, "/search")}
            onClick={(e) => {
              e.preventDefault();
              setSearchOpen(true);
            }}
            title={t.assistant.findAsk}
            className="text-teal hover:bg-light-gray/40 inline-flex size-11 items-center justify-center rounded-full transition-colors sm:hidden"
          >
            <Search className="size-5" aria-hidden />
            <span className="sr-only">{t.assistant.findAsk}</span>
          </Link>
        ) : (
          <Link
            href={langPath(lang, "/search")}
            title={t.search.title}
            className="text-teal hover:bg-light-gray/40 inline-flex size-11 items-center justify-center rounded-full transition-colors sm:hidden"
          >
            <Search className="size-5" aria-hidden />
            <span className="sr-only">{t.search.title}</span>
          </Link>
        )}
        <div className="hidden md:block">
          <LangToggle current={lang} tone="app" languages={languages} />
        </div>
        {issueReports ? (
          <ReportProblem
            lang={lang}
            portalName={portalName}
            className="text-teal hover:bg-light-gray/40 inline-flex size-11 items-center justify-center rounded-full transition-colors"
          />
        ) : null}
        <Link
          href={langPath(lang, "/checkout")}
          title={t.appNav.cart}
          className="text-teal hover:bg-light-gray/40 relative inline-flex size-11 items-center justify-center rounded-full transition-colors"
        >
          <ShoppingCart className="size-5" aria-hidden />
          {/* sr-only text instead of aria-label so the streamed count badge
              is announced too ("Košarica 2"). */}
          <span className="sr-only">{t.appNav.cart}</span>
          {cartBadge}
        </Link>
        <AccountMenu
          lang={lang}
          name={userName ?? t.nav.profile}
          email={userEmail}
          isSubscription={isSubscription}
        />
      </div>
      {aiChatEnabled ? (
        // The app shell renders for signed-in users only — the assistant row
        // always gets the direct href here.
        <SearchModal
          lang={lang}
          isAuthed
          open={searchOpen}
          onOpen={() => setSearchOpen(true)}
          onClose={() => setSearchOpen(false)}
        />
      ) : null}
    </>
  );
}
